/**
 * Preferred time parser utility
 * Parses Korean weekday and time strings from lesson requests
 */

export interface ParsedTimeOption {
  dayOfWeek: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
  time: string; // HH:mm format
  label: string; // Korean display label
}

const DAY_MAP: Record<string, ParsedTimeOption['dayOfWeek']> = {
  '월': 'monday',
  '화': 'tuesday',
  '수': 'wednesday',
  '목': 'thursday',
  '금': 'friday',
  '토': 'saturday',
  '일': 'sunday',
};

/**
 * Parse preferred time string for regular lessons
 * Extracts weekday and time information
 *
 * @param preferredTime - Preferred time string (e.g., "수요일: 13:00 | 목요일: 14:00, 17:00")
 * @returns Array of parsed time options with dayOfWeek, time, and label
 *
 * @example
 * parsePreferredTimesForLesson("수요일: 13:00 | 목요일: 14:00, 17:00")
 * // Returns:
 * // [
 * //   { dayOfWeek: 'wednesday', time: '13:00', label: '수요일 13:00' },
 * //   { dayOfWeek: 'thursday', time: '14:00', label: '목요일 14:00' },
 * //   { dayOfWeek: 'thursday', time: '17:00', label: '목요일 17:00' }
 * // ]
 */
export const parsePreferredTimesForLesson = (preferredTime: string): ParsedTimeOption[] => {
  const options: ParsedTimeOption[] = [];

  if (!preferredTime || typeof preferredTime !== 'string') {
    return options;
  }

  // Split by pipe (|) to get different day-time groups
  const dayTimeGroups = preferredTime.split('|').map(t => t.trim());

  dayTimeGroups.forEach((group) => {
    // Match pattern: "요일: HH:mm"
    const match = group.match(/(월|화|수|목|금|토|일)요일:\s*(.+)/);
    if (!match) return;

    const [, dayChar, times] = match;
    const dayOfWeek = DAY_MAP[dayChar];
    if (!dayOfWeek) return;

    // Split times by comma for multiple times on same day
    const timeList = times.split(',').map(t => t.trim());

    timeList.forEach(timeStr => {
      // Match time pattern: HH:mm
      const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})/);
      if (!timeMatch) return;

      const [, hour, minute] = timeMatch;
      const formattedTime = `${hour.padStart(2, '0')}:${minute}`;
      const label = `${dayChar}요일 ${formattedTime}`;

      options.push({
        dayOfWeek,
        time: formattedTime,
        label,
      });
    });
  });

  return options;
};

export interface ParsedOneDayTimeOption {
  date: string; // YYYY-MM-DD format
  time: string; // HH:mm format
  label: string; // Korean display label
}

/**
 * Parse preferred time string for one-day classes
 * Extracts date and time information
 *
 * @param preferredTime - Preferred time string (e.g., "2024-03-15 14:00 | 2024-03-16 10:00")
 * @returns Array of parsed time options with date, time, and label
 *
 * @example
 * parsePreferredTimesForOneDay("2024-03-15 14:00 | 2024-03-16 10:00")
 * // Returns:
 * // [
 * //   { date: '2024-03-15', time: '14:00', label: '2024-03-15 14:00' },
 * //   { date: '2024-03-16', time: '10:00', label: '2024-03-16 10:00' }
 * // ]
 */
export const parsePreferredTimesForOneDay = (preferredTime: string): ParsedOneDayTimeOption[] => {
  const options: ParsedOneDayTimeOption[] = [];

  if (!preferredTime || typeof preferredTime !== 'string') {
    return options;
  }

  // Split by pipe (|) or newline
  const dateTimeGroups = preferredTime.split(/[|\n]/).map(t => t.trim()).filter(Boolean);

  dateTimeGroups.forEach((group) => {
    // Match pattern: YYYY-MM-DD HH:mm or YYYY/MM/DD HH:mm
    const match = group.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})\s+(\d{1,2}):(\d{2})/);
    if (!match) return;

    const [, year, month, day, hour, minute] = match;
    const date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    const time = `${hour.padStart(2, '0')}:${minute}`;
    const label = `${year}년 ${parseInt(month)}월 ${parseInt(day)}일 ${time}`;

    options.push({
      date,
      time,
      label,
    });
  });

  return options;
};

/**
 * Generic parser that auto-detects type and parses accordingly
 * @param preferredTime - Preferred time string
 * @param type - 'lesson' for regular lessons, 'oneday' for one-day classes
 * @returns Parsed time options
 */
export const parsePreferredTimes = (
  preferredTime: string,
  type: 'lesson' | 'oneday' = 'lesson'
): ParsedTimeOption[] | ParsedOneDayTimeOption[] => {
  return type === 'lesson'
    ? parsePreferredTimesForLesson(preferredTime)
    : parsePreferredTimesForOneDay(preferredTime);
};
